import { createReadStream, createWriteStream } from 'fs';
import { promises as fs } from 'fs';
import path from 'path';
import { PathnameService } from '../services/pathname-service.js';
import { logger } from '../utils/logger.js';

export interface FileSystemAdapter {
  readFile(path: string): Promise<string>;
  writeFile(path: string, data: string): Promise<void>;
  exists(path: string): Promise<boolean>;
  pathExists(path: string): Promise<boolean>;
  ensureDir(path: string): Promise<void>;
  readdir(path: string): Promise<string[]>;
  move(src: string, dest: string): Promise<void>;
  remove(path: string): Promise<void>;
  createReadStream(path: string): NodeJS.ReadableStream;
  createWriteStream(path: string): NodeJS.WritableStream;
  stat(path: string): Promise<{ isDirectory(): boolean }>;
  rename(oldPath: string, newPath: string): Promise<void>;
  mkdtemp(prefix: string): Promise<string>;
  rm(path: string, options: { recursive?: boolean; force?: boolean }): Promise<void>;
}

// Real filesystem implementation
export const realFileSystemAdapter: FileSystemAdapter = {
  async readFile(path: string): Promise<string> {
    return fs.readFile(path, 'utf8');
  },

  async writeFile(path: string, data: string): Promise<void> {
    return fs.writeFile(path, data, 'utf8');
  },

  async exists(path: string): Promise<boolean> {
    try {
      await fs.access(path);
      return true;
    } catch {
      return false;
    }
  },

  async pathExists(path: string): Promise<boolean> {
    return this.exists(path);
  },

  async ensureDir(path: string): Promise<void> {
    await fs.mkdir(path, { recursive: true });
  },

  async readdir(path: string): Promise<string[]> {
    return fs.readdir(path);
  },

  async move(src: string, dest: string): Promise<void> {
    await fs.rename(src, dest);
  },

  async remove(path: string): Promise<void> {
    await fs.unlink(path);
  },

  createReadStream(path: string): NodeJS.ReadableStream {
    return createReadStream(path);
  },

  createWriteStream(path: string): NodeJS.WritableStream {
    return createWriteStream(path);
  },

  async stat(path: string): Promise<{ isDirectory(): boolean }> {
    const stats = await fs.stat(path);
    return {
      isDirectory: () => stats.isDirectory()
    };
  },

  async rename(oldPath: string, newPath: string): Promise<void> {
    await fs.rename(oldPath, newPath);
  },

  async mkdtemp(prefix: string): Promise<string> {
    return fs.mkdtemp(path.join(prefix, ''));
  },

  async rm(path: string, options: { recursive?: boolean; force?: boolean }): Promise<void> {
    await fs.rm(path, options);
  }
};

/**
 * Wraps a FileSystemAdapter to handle path resolution relative to a root directory.
 * This ensures all paths passed to the underlying adapter are properly prefixed with the root directory.
 */
export class RootedFileSystemAdapter implements FileSystemAdapter {
  constructor(
    private fs: FileSystemAdapter,
    private rootDir: string,
    private pathnameService?: PathnameService
  ) {
    // Ensure rootDir is absolute and normalized
    this.rootDir = path.resolve(rootDir);
    logger.debug('RootedFileSystemAdapter initialized with rootDir:', this.rootDir);
  }

  private resolvePath(filePath: string): string {
    // If pathname service is available, use it to canonicalize the path
    if (this.pathnameService) {
      filePath = this.pathnameService.toCanonicalPath(filePath);
    }

    // Normalize the input path
    const normalizedPath = path.normalize(filePath);
    
    // If it's already absolute
    if (path.isAbsolute(normalizedPath)) {
      // If it's already under our root dir, return it as is
      if (normalizedPath.startsWith(this.rootDir)) {
        return normalizedPath;
      }
      
      // For any other absolute path, get just the relative portion
      const projectRoot = path.resolve(process.cwd());
      let relativePath;
      
      if (normalizedPath.startsWith(projectRoot)) {
        // Get path relative to project root
        relativePath = path.relative(projectRoot, normalizedPath);
      } else {
        // For paths outside project root, use the path relative to root directory
        relativePath = path.relative('/', normalizedPath);
      }
      
      // Remove any leading directory components that match our root dir structure
      const rootParts = this.rootDir.split(path.sep).filter(Boolean);
      const pathParts = relativePath.split(path.sep).filter(Boolean);
      
      // Find where the paths diverge
      let i = 0;
      while (i < rootParts.length && i < pathParts.length && rootParts[i] === pathParts[i]) {
        i++;
      }
      
      // Use only the unique part of the path
      const uniquePath = pathParts.slice(i).join(path.sep);
      
      return path.join(this.rootDir, uniquePath);
    }
    
    // For relative paths, just join with root
    return path.join(this.rootDir, normalizedPath);
  }

  async readFile(filePath: string): Promise<string> {
    return this.fs.readFile(this.resolvePath(filePath));
  }

  async writeFile(filePath: string, content: string): Promise<void> {
    return this.fs.writeFile(this.resolvePath(filePath), content);
  }

  async exists(filePath: string): Promise<boolean> {
    return this.fs.exists(this.resolvePath(filePath));
  }

  async pathExists(filePath: string): Promise<boolean> {
    return this.fs.pathExists(this.resolvePath(filePath));
  }

  async ensureDir(dirPath: string): Promise<void> {
    return this.fs.ensureDir(this.resolvePath(dirPath));
  }

  async readdir(dirPath: string): Promise<string[]> {
    return this.fs.readdir(this.resolvePath(dirPath));
  }

  async move(src: string, dest: string): Promise<void> {
    return this.fs.move(this.resolvePath(src), this.resolvePath(dest));
  }

  async remove(filePath: string): Promise<void> {
    return this.fs.remove(this.resolvePath(filePath));
  }

  createReadStream(filePath: string): NodeJS.ReadableStream {
    return this.fs.createReadStream(this.resolvePath(filePath));
  }

  createWriteStream(filePath: string): NodeJS.WritableStream {
    return this.fs.createWriteStream(this.resolvePath(filePath));
  }

  async stat(filePath: string): Promise<{ isDirectory(): boolean }> {
    return this.fs.stat(this.resolvePath(filePath));
  }

  async rename(oldPath: string, newPath: string): Promise<void> {
    return this.fs.rename(this.resolvePath(oldPath), this.resolvePath(newPath));
  }

  async mkdtemp(prefix: string): Promise<string> {
    return this.fs.mkdtemp(this.resolvePath(prefix));
  }

  async rm(path: string, options: { recursive?: boolean; force?: boolean }): Promise<void> {
    return this.fs.rm(this.resolvePath(path), options);
  }
} 